import React, { ComponentType, useCallback } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useIntl } from 'react-intl';
import ButtonWithIcon, { SocialLoginButtonContainer }
  from '@fuww/library/src/Login/ButtonWithIcon';
import hiddenLoading from '../../lib/hiddenLoading';
import messages from '../../lib/messages.mjs';
import { useSiteContext } from '../SiteContext';
import { Registrar } from '../../lib/graphql/api/graphql';
import getRegistrarFromRoute from '../../lib/getRegistrarFromRoute';

interface TokenResponse {
  data?: {
    access_token?: string;
  };
}

const defaultGetAccessToken = (
  response: TokenResponse,
): string | undefined => response.data?.access_token;

interface SocialLoginComponentProperties {
  children: React.ReactNode;
  onLoginStart?: () => void;
  onResolve?: (response: unknown) => void;
  onReject?: (error: unknown) => void;
  isOnlyGetToken?: boolean;
  redirect_uri?: string;
  [key: string]: unknown;
}

interface Provider {
  iconUrl: string;
  label: string;
  providerArgument: string;
  Component: ComponentType<SocialLoginComponentProperties>;
  properties: Record<string, unknown>;
  getAccessToken: (response: TokenResponse) => string | undefined;
}

type ProvidersMap = Record<'google' | 'linkedin', Provider>;

export const providers: ProvidersMap = {
  google: {
    iconUrl:
    'https://media.fashionunited.com/logos/google__1_.HubF6pFQ92pELpfAC.svg',
    label: 'Google',
    providerArgument: 'GOOGLE',
    Component: dynamic(
      () => import('reactjs-social-login')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((module) => module.LoginSocialGoogle) as any, {
        ssr: false,
        loading: hiddenLoading,
      },
    ) as ComponentType<SocialLoginComponentProperties>,
    properties: {
      // eslint-disable-next-line max-len
      client_id: '113803436273-2ejsbcb6b3ql3apabqv71rtuj5s4r01m.apps.googleusercontent.com',
      scope: 'openid profile email',
      discoveryDocs: 'claims_supported',
    },
    getAccessToken: defaultGetAccessToken,
  },
  linkedin: {
    iconUrl:
    'https://media.fashionunited.com/media/logos/LinkedIn-Bug.svg',
    label: 'LinkedIn',
    providerArgument: 'LINKEDIN',
    Component: dynamic(
      () => import('reactjs-social-login')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((module) => module.LoginSocialLinkedin) as any, {
        ssr: false,
        loading: hiddenLoading,
      },
    ) as ComponentType<SocialLoginComponentProperties>,
    properties: {
      client_id: '78nv4on1akw07d',
      client_secret: 'WPL_AP1.PCfQXHA90lIhIDsh.8CMrMQ==',
      scope: 'openid profile email',
    },
    getAccessToken: defaultGetAccessToken,
  },
};

interface LoginButtonProperties {
  provider: keyof ProvidersMap;
  onLogin: () => void;
  onLoginSuccess: (data: {
    accessToken?: string;
    providerArgument: string;
    registeredVia: Registrar;
  }) => Promise<void> | void;
  setErrorMessage: (message: string) => void;
  setDisabled: (disabled: boolean) => void;
  disabled?: boolean;
  labelKey?: string;
}

export function LoginButton({
  provider,
  onLogin,
  onLoginSuccess,
  setErrorMessage,
  setDisabled,
  disabled = false,
  labelKey = 'login.logInWithProvider',
  ...rest
}: LoginButtonProperties) {
  const intl = useIntl();
  const { url } = useSiteContext();
  const { route } = useRouter();

  const {
    Component,
    properties,
    iconUrl,
    label,
    getAccessToken,
    providerArgument,
  } = providers[provider];

  return (
    // workaround for disabling is not supported by reactjs-social-login
    <SocialLoginButtonContainer disabled={disabled}>
      <Component
        onLoginStart={onLogin}
        onResolve={useCallback(
          (response: unknown) => onLoginSuccess({
            accessToken: getAccessToken(response as TokenResponse),
            providerArgument,
            registeredVia: getRegistrarFromRoute(route),
          }),
          [onLoginSuccess, getAccessToken, providerArgument, route],
        )}
        onReject={useCallback((error: unknown) => {
          const errorMessage = typeof error === 'string'
            ? error
            : (error as { data?: { message?: string } }).data?.message
              ?? 'Login failed';
          setErrorMessage(errorMessage);
          setDisabled(false);
        }, [setErrorMessage, setDisabled])}
        isOnlyGetToken
        redirect_uri={url}
        {...properties}
      >
        <ButtonWithIcon
          icon={iconUrl}
          alt={`${label} logo`}
          disabled={disabled}
          className={`social-login-${provider}`}
          text={
            intl.formatMessage(messages[labelKey], {
              provider: label,
            })
          }
          {...rest}
        />
      </Component>
    </SocialLoginButtonContainer>
  );
}

export default LoginButton;
